import _ from '@/assets/lib/my-lodash'
import educationalOptionsInitialJson from './educationalOptionsInitial.json'
import educationalPeriodDefaultsJson from './educationalPeriodDefaults.json'

export type EducationalPeriodOption = {
  name?: string
  defaultName?: string
  period?: number | string
  display?: boolean
  displayEnter?: boolean
  displayGraduate?: boolean
  enable?: boolean
  enterMonth?: number
  enterLabel?: string
  graduateMonth?: number
  graduateLabel?: string
  order?: number
}

export type EducationalOptions = {
  lastEducationalBackground: string
  periods: Record<string, EducationalPeriodOption>
}

export type RirekishoListItem = {
  year: number
  month: number
  desc: string
}

type EducationalPeriodDefaults = {
  higherEducations: string[]
  periods: Record<string, EducationalPeriodOption>
}

export function createInitialEducationalOptions(): EducationalOptions {
  return JSON.parse(JSON.stringify(educationalOptionsInitialJson))
}

type NormalizedPeriodItem = {
  enable: boolean
  display: boolean
  displayEnter: boolean
  displayGraduate: boolean
  period: number
  name: string
  enterMonth: number
  enterLabel: string
  graduateMonth: number
  graduateLabel: string
}

function normalizePeriodItem(
  itemOptions: EducationalPeriodOption
): NormalizedPeriodItem {
  return {
    enable: itemOptions.enable || false,
    display: itemOptions.display || false,
    displayEnter: itemOptions.displayEnter ?? true,
    displayGraduate: itemOptions.displayGraduate ?? true,
    period: Number(itemOptions.period) || 0,
    name: itemOptions.name || '',
    enterMonth: itemOptions.enterMonth || 4,
    enterLabel: itemOptions.enterLabel || '入学',
    graduateMonth: itemOptions.graduateMonth || 3,
    graduateLabel: itemOptions.graduateLabel || '卒業'
  }
}

function calcJuniorHighEnterYear(
  birthYear: number,
  birthMonth: number,
  birthDay: number
): number {
  // 中学入学年度を計算（早生まれは１年遅れる）
  if (birthMonth < 4 || (birthMonth === 4 && birthDay === 1)) {
    return birthYear + 12
  }
  return birthYear + 13
}

function applyHigherEducationEnable(
  periods: Record<string, EducationalPeriodOption>,
  higherEducations: string[],
  lastEducationalBackgroundKey: string
) {
  for (const key of higherEducations) {
    if (!periods[key]) continue
    periods[key].enable = false
  }
  if (periods[lastEducationalBackgroundKey]) {
    periods[lastEducationalBackgroundKey].enable = true
  }
  if (lastEducationalBackgroundKey === 'master') {
    periods.university.enable = true
  }
  if (lastEducationalBackgroundKey === 'docter') {
    periods.university.enable = true
    periods.master.enable = true
  }
}

export function buildRirekishoList(params: {
  birthYear: number | string
  birthMonth: number | string
  birthDay: number | string
  options: EducationalOptions
}): RirekishoListItem[] {
  const birthYear = Number(params.birthYear)
  const birthMonth = Number(params.birthMonth)
  const birthDay = Number(params.birthDay)

  const defaults: EducationalPeriodDefaults = JSON.parse(
    JSON.stringify(educationalPeriodDefaultsJson)
  )
  // 生きた options（reactive）は変更せず、クローン上でのみ defaultsDeep する。
  // Vue 2.7 の reactive() は defaultsDeep が新規に足すキーを非リアクティブにしてしまうため。
  const merged: EducationalOptions = _.defaultsDeep(
    JSON.parse(JSON.stringify(params.options)),
    { periods: defaults.periods }
  )

  let lastEducationalBackground =
    merged.lastEducationalBackground || 'university'
  // 高専は高校を上書きして使うため高校に戻す
  if (lastEducationalBackground === 'instituteOfTechnologyCollege') {
    lastEducationalBackground = 'highSchool'
  }
  applyHigherEducationEnable(
    merged.periods,
    defaults.higherEducations,
    lastEducationalBackground
  )

  const rirekishoList: RirekishoListItem[] = []
  let year = calcJuniorHighEnterYear(birthYear, birthMonth, birthDay)
  const orderedPeriods = _.orderBy(merged.periods, ['order'], ['asc'])
  // 元コードは _.orderBy が返す配列を for...in で回しており、
  // key は配列インデックスの文字列だったため lastEducationalBackground との
  // 一致比較（break 用）は実質発火しなかった。enable フィルタのみで元と同じ出力になる。
  for (const rawItem of orderedPeriods) {
    const item = normalizePeriodItem(rawItem)
    if (!item.enable) continue
    if (item.display && item.displayEnter) {
      rirekishoList.push({
        year,
        month: item.enterMonth,
        desc: item.name + ' ' + item.enterLabel
      })
    }
    year = year + item.period
    if (item.display && item.displayGraduate) {
      rirekishoList.push({
        year,
        month: item.graduateMonth,
        desc: item.name + ' ' + item.graduateLabel
      })
    }
  }
  return rirekishoList
}
